import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import { isMaxNumberOfUsersReached } from 'utils/VisitorGroup/VisitorGroup';
import { canManageUsersInVisitorGroup } from 'v2/utils/visitorGroupOrigin';

export const AddUsersToGroupsSelectionState = {
  Available: 'available',
  AlreadyAssigned: 'alreadyAssigned',
  LimitReached: 'limitReached'
} as const;

export type AddUsersToGroupsSelectionState =
  (typeof AddUsersToGroupsSelectionState)[keyof typeof AddUsersToGroupsSelectionState];

export interface GroupSelectionState {
  state: AddUsersToGroupsSelectionState;
  selectedUserIdsAlreadyInGroup: string[];
}

export interface SelectAllState {
  checked: boolean;
  indeterminate: boolean;
}

export const getGroupSelectionState = (visitorGroup: VisitorGroup, selectedUserIds: string[]): GroupSelectionState => {
  const memberIds = new Set(visitorGroup.members.map((member) => member.id));
  const selectedUserIdsAlreadyInGroup = selectedUserIds.filter((visitorId) => memberIds.has(visitorId));
  const usersToAddCount = selectedUserIds.length - selectedUserIdsAlreadyInGroup.length;

  if (usersToAddCount === 0) {
    return { state: AddUsersToGroupsSelectionState.AlreadyAssigned, selectedUserIdsAlreadyInGroup };
  }

  const remainingCapacity =
    visitorGroup.maxNumberOfMembers == null ? undefined : visitorGroup.maxNumberOfMembers - visitorGroup.members.length;

  if (
    isMaxNumberOfUsersReached(visitorGroup) ||
    (remainingCapacity !== undefined && remainingCapacity < usersToAddCount)
  ) {
    return { state: AddUsersToGroupsSelectionState.LimitReached, selectedUserIdsAlreadyInGroup };
  }

  return { state: AddUsersToGroupsSelectionState.Available, selectedUserIdsAlreadyInGroup };
};

export const getSelectableGroupIds = (visitorGroups: VisitorGroup[], selectedUserIds: string[]) =>
  visitorGroups
    .filter(canManageUsersInVisitorGroup)
    .filter(
      (visitorGroup) =>
        getGroupSelectionState(visitorGroup, selectedUserIds).state === AddUsersToGroupsSelectionState.Available
    )
    .map((visitorGroup) => visitorGroup.id);

export const getSelectAllState = (visibleSelectableGroupIds: string[], selectedGroupIds: string[]): SelectAllState => {
  const selectedGroupIdSet = new Set(selectedGroupIds);
  const selectedCount = visibleSelectableGroupIds.filter((groupId) => selectedGroupIdSet.has(groupId)).length;

  return {
    checked: visibleSelectableGroupIds.length > 0 && selectedCount === visibleSelectableGroupIds.length,
    indeterminate: selectedCount > 0 && selectedCount < visibleSelectableGroupIds.length
  };
};

export const toggleVisibleSelectableGroupIds = (
  selectedGroupIds: string[],
  visibleSelectableGroupIds: string[]
): string[] => {
  const { checked } = getSelectAllState(visibleSelectableGroupIds, selectedGroupIds);

  return checked
    ? selectedGroupIds.filter((groupId) => !visibleSelectableGroupIds.includes(groupId))
    : [...selectedGroupIds, ...visibleSelectableGroupIds.filter((groupId) => !selectedGroupIds.includes(groupId))];
};
